import { getFilterDefinition } from '../../../filters/engine/filterRegistry';
import { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type { Page, ReportPageConfig } from '../../PageConfigProvider/types';

export const defaultReportParameterFilters = [
  FilterId.INDUSTRY,
  FilterId.COMPANY,
  FilterId.GEOGRAPHY,
  FilterId.ROLE,
  FilterId.GENDER,
  FilterId.ETHNICITY,
  FilterId.SENIORITY,
  FilterId.CUSTOM_SKILL,
  FilterId.KEYWORDS,
];

export const filterCanBePrimary = (filterId: FilterId) => {
  return (
    filterId === FilterId.INDUSTRY ||
    filterId === FilterId.COMPANY ||
    filterId === FilterId.GEOGRAPHY ||
    filterId === FilterId.ROLE
  );
};

export const toReadableFilterName = (filterId: FilterId) => {
  const def = getFilterDefinition(filterId);
  if (def?.name) return def.name;
  return filterId
    .toLowerCase()
    .split('_')
    .map((s) => s[0]?.toUpperCase() + s.slice(1))
    .join(' ');
};

export const determinePrimaryParameter = (
  parameters: ReportParameterConfig[]
) => {
  if (!parameters || parameters.length === 0) return parameters;

  const primaryIndexes = parameters.reduce<number[]>((acc, p, i) => {
    if (p.isPrimary) acc.push(i);
    return acc;
  }, []);

  // If more than one primary, keep the first and demote others
  if (primaryIndexes.length > 1) {
    console.error(
      'Multiple primary report parameters detected; keeping the first and demoting the rest.'
    );
    const keepIndex = primaryIndexes[0];
    return parameters.map((p, i) => ({ ...p, isPrimary: i === keepIndex }));
  }

  // If none are primary, prefer COMPANY, GEOGRAPHY, then ROLE
  if (primaryIndexes.length === 0) {
    const targetIndex = parameters.findIndex((p) =>
      filterCanBePrimary(p.filterId)
    );
    if (targetIndex !== -1) {
      return parameters.map((p, i) => ({ ...p, isPrimary: i === targetIndex }));
    }
    // If no company/geo/role present, do nothing
    return parameters;
  }

  // Exactly one primary already
  return parameters;
};

export const parametersAreValid = (
  parameters: ReportParameterConfig[]
): {
  isValid: boolean;
  reason: string | null;
} => {
  if (!parameters || parameters.length === 0) {
    return { isValid: false, reason: 'At least one parameter is required' };
  }

  // Verify exactly one primary parameter and that it is a valid primary filter
  const primaryParams = parameters.filter((p) => p.isPrimary);
  const primary = primaryParams[0];
  if (
    primaryParams.length !== 1 ||
    !primary ||
    !filterCanBePrimary(primary?.filterId)
  ) {
    return {
      isValid: false,
      reason: 'Reports must have one unit of analysis.',
    };
  }

  return { isValid: true, reason: null };
};

export const parametersFromPageConfig = (
  pageConfig: ReportPageConfig | Page
) => {
  const tabs = pageConfig?.tabs ?? [];
  const all = tabs.flatMap(
    (t: { tabContent: { reportParameters?: ReportParameterConfig[] } }) =>
      t.tabContent.reportParameters ?? []
  );
  const seen = new Set<string>();
  return all.filter((p) => {
    if (seen.has(p.parameterId)) return false;
    seen.add(p.parameterId);
    return true;
  });
};
